import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
// We can reuse Linear CSS or create a new one. Let's create a new one for clean separation though they are similar.
import './LinearIntegration.css';

const JiraMappings = () => {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [workspaceName, setWorkspaceName] = useState('');
    const [projects, setProjects] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [apps, setApps] = useState([]);
    const [syncing, setSyncing] = useState(false);
    const [editingMapping, setEditingMapping] = useState(null);

    useEffect(() => {
        if (currentWorkspace) {
            checkStatus();
            fetchApps();
        }
    }, [currentWorkspace]);

    const checkStatus = async () => {
        try {
            const response = await api.get('/integrations/jira/status', {
                params: { workspaceId: currentWorkspace.id }
            });

            if (response.data.connected) {
                setConnected(true);
                setWorkspaceName(response.data.workspaceName);
                await fetchMappings();
            } else {
                setConnected(false);
            }
        } catch (error) {
            console.error('Error checking Jira status:', error);
            setConnected(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchApps = async () => {
        try {
            const response = await api.get('/apps');
            setApps(response.data.rows || []);
        } catch (error) {
            console.error('Error fetching apps:', error);
        }
    };

    const fetchMappings = async () => {
        try {
            const response = await api.get('/integrations/jira/mappings', {
                params: { workspaceId: currentWorkspace.id }
            });
            setMappings(response.data.mappings || []);
        } catch (error) {
            console.error('Error fetching mappings:', error);
        }
    };

    const handleSyncProjects = async () => {
        setSyncing(true);
        try {
            const response = await api.post('/integrations/jira/projects/sync', {
                workspaceId: currentWorkspace.id
            });
            setProjects(response.data.projects || []);
        } catch (error) {
            console.error('Error syncing projects:', error);
            alert('Failed to sync projects from Jira');
        } finally {
            setSyncing(false);
        }
    };

    const handleCreateMapping = async (projectId, projectName, projectKey) => {
        setEditingMapping({
            jiraProjectId: projectId,
            jiraProjectName: projectName,
            jiraProjectKey: projectKey,
            appId: '',
            defaultPlatform: '',
            defaultEnvironment: 'staging',
            defaultDeviceProvider: 'aws-remote-access',
            priority: 0
        });
    };

    const handleSaveMapping = async () => {
        if (!editingMapping.appId) {
            alert('Please select an app');
            return;
        }

        try {
            const response = await api.post('/integrations/jira/mappings', {
                workspaceId: currentWorkspace.id,
                ...editingMapping
            });

            setMappings([...mappings, response.data.mapping]);
            setEditingMapping(null);
        } catch (error) {
            console.error('Error creating mapping:', error);
            alert('Failed to create mapping');
        }
    };

    // Note: Update not yet strictly implemented in backend but route exists
    const handleUpdateMapping = async (mappingId, updates) => {
        try {
            // Check if backend supports update for Jira (it likely uses similar logic)
            // For now assuming Delete/Re-create workflow or simple update if implemented
            alert('Update not fully implemented yet, please delete and recreate for now.');
        } catch (error) {
            console.error('Error updating mapping:', error);
            alert('Failed to update mapping');
        }
    };

    const handleDeleteMapping = async (mappingId) => {
        if (!window.confirm('Are you sure you want to delete this mapping?')) {
            return;
        }

        try {
            await api.delete(`/integrations/jira/mappings/${mappingId}`);
            setMappings(mappings.filter(m => m.id !== mappingId));
        } catch (error) {
            console.error('Error deleting mapping:', error);
            alert('Failed to delete mapping');
        }
    };

    const getProjectMappings = (projectId) => {
        return mappings.filter(m => m.jiraProjectId === projectId && m.isActive);
    };

    if (loading) {
        return (
            <div className="linear-integration-container">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (!connected) {
        return (
            <div className="linear-integration-container">
                <div className="not-connected">
                    <div className="not-connected-icon">🔗</div>
                    <h2>Jira Not Connected</h2>
                    <p>Please connect Jira from the Integrations page first</p>
                    <button
                        className="btn-primary"
                        onClick={() => navigate('/integrations')}
                    >
                        Go to Integrations
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="linear-integration-container">
            <div className="linear-header">
                <button className="back-button" onClick={() => navigate('/integrations')}>
                    ← Back to Integrations
                </button>
                <div className="header-content">
                    <div className="header-left">
                        <h1>Jira Configuration</h1>
                        <p>Connected to: <strong>{workspaceName}</strong></p>
                    </div>
                    <button
                        className="btn-secondary"
                        onClick={handleSyncProjects}
                        disabled={syncing}
                    >
                        {syncing ? '🔄 Syncing...' : '🔄 Sync Projects'}
                    </button>
                </div>
            </div>

            <div className="info-banner">
                <div className="banner-icon">💡</div>
                <div className="banner-content">
                    <strong>How it works:</strong> Map your Jira projects to Rova apps.
                    Simply comment <code>@rova</code> or <code>Rova:</code> in a Jira issue to automatically
                    run tests on the mapped app.
                </div>
            </div>

            {projects.length === 0 && mappings.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No Projects Found</h3>
                    <p>Sync your Jira projects to create mappings</p>
                    <button
                        className="btn-primary"
                        onClick={handleSyncProjects}
                        disabled={syncing}
                    >
                        {syncing ? 'Syncing...' : 'Sync Jira Projects'}
                    </button>
                </div>
            ) : (
                <>
                    {/* Existing Mappings */}
                    {mappings.length > 0 && (
                        <div className="mappings-section">
                            <h2>Active Mappings</h2>
                            <div className="mappings-list">
                                {mappings.map(mapping => (
                                    <div key={mapping.id} className="mapping-card">
                                        <div className="mapping-header">
                                            <div className="mapping-project">
                                                <span className="project-icon">📋</span>
                                                <span className="project-name">{mapping.jiraProjectName} ({mapping.jiraProjectKey})</span>
                                            </div>
                                            <button
                                                className="delete-button"
                                                onClick={() => handleDeleteMapping(mapping.id)}
                                                title="Delete mapping"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                        <div className="mapping-body">
                                            <div className="mapping-row">
                                                <span className="label">App:</span>
                                                <span className="value">
                                                    {mapping.app?.displayName || mapping.app?.name}
                                                    <span className="platform-badge">
                                                        {mapping.app?.platform === 'android' ? '🤖' : '🍎'}
                                                        {mapping.app?.platform}
                                                    </span>
                                                </span>
                                            </div>
                                            {mapping.defaultPlatform && (
                                                <div className="mapping-row">
                                                    <span className="label">Platform:</span>
                                                    <span className="value">{mapping.defaultPlatform}</span>
                                                </div>
                                            )}
                                            {mapping.defaultEnvironment && (
                                                <div className="mapping-row">
                                                    <span className="label">Environment:</span>
                                                    <span className="value">{mapping.defaultEnvironment}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Available Projects */}
                    {projects.length > 0 && (
                        <div className="projects-section">
                            <h2>📋 Jira Projects</h2>
                            <div className="projects-list">
                                {projects.map(project => {
                                    const projectMappings = getProjectMappings(project.id);
                                    return (
                                        <div key={project.id} className="project-card">
                                            <div className="project-header">
                                                <div className="project-info">
                                                    <h3>{project.name} ({project.key})</h3>
                                                </div>
                                                {projectMappings.length > 0 && (
                                                    <div className="mapping-count">
                                                        {projectMappings.length} mapping{projectMappings.length !== 1 ? 's' : ''}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="btn-primary-small"
                                                onClick={() => handleCreateMapping(project.id, project.name, project.key)}
                                            >
                                                + Add Mapping
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Mapping Editor Modal */}
            {editingMapping && (
                <div className="modal-overlay" onClick={() => setEditingMapping(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create Mapping</h2>
                            <button className="close-button" onClick={() => setEditingMapping(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Jira Project</label>
                                <input
                                    type="text"
                                    value={editingMapping.jiraProjectName}
                                    disabled
                                    className="form-input disabled"
                                />
                            </div>

                            <div className="form-group">
                                <label>Rova App *</label>
                                <select
                                    className="form-select"
                                    value={editingMapping.appId}
                                    onChange={(e) => setEditingMapping({ ...editingMapping, appId: e.target.value })}
                                >
                                    <option value="">Select an app...</option>
                                    {apps.map(app => (
                                        <option key={app.id} value={app.id}>
                                            {app.displayName || app.name} ({app.platform})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Default Platform</label>
                                <select
                                    className="form-select"
                                    value={editingMapping.defaultPlatform}
                                    onChange={(e) => setEditingMapping({ ...editingMapping, defaultPlatform: e.target.value })}
                                >
                                    <option value="">Auto-detect from app</option>
                                    <option value="android">Android</option>
                                    <option value="ios">iOS</option>
                                    <option value="web">Web</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Default Environment</label>
                                <select
                                    className="form-select"
                                    value={editingMapping.defaultEnvironment}
                                    onChange={(e) => setEditingMapping({ ...editingMapping, defaultEnvironment: e.target.value })}
                                >
                                    <option value="development">Development</option>
                                    <option value="staging">Staging</option>
                                    <option value="production">Production</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Device Provider</label>
                                <select
                                    className="form-select"
                                    value={editingMapping.defaultDeviceProvider}
                                    onChange={(e) => setEditingMapping({ ...editingMapping, defaultDeviceProvider: e.target.value })}
                                >
                                    <option value="aws-remote-access">AWS Device Farm</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Priority</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={editingMapping.priority}
                                    onChange={(e) => setEditingMapping({ ...editingMapping, priority: parseInt(e.target.value) })}
                                    placeholder="0"
                                />
                                <small className="form-hint">Higher priority mappings are used first when multiple exist</small>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setEditingMapping(null)}>
                                Cancel
                            </button>
                            <button className="btn-primary" onClick={handleSaveMapping}>
                                Create Mapping
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JiraMappings;
